import { 
  Injectable, 
  ApplicationRef, 
  createComponent, 
  EnvironmentInjector,
  ComponentRef,
  EmbeddedViewRef,
  inject,
  effect,
  computed
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  CancelDialogService,
  CreateCustomDialogService,
  CreateInitiativeDialogService,
  ExceedTicketDialogService
} from '@sml/utility';

@Injectable({ providedIn: 'root' })
export class DialogManagerLoaderService {
  private componentRef: ComponentRef<any> | null = null;
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly cancelDialogService = inject(CancelDialogService);
  private readonly createCustomFormService = inject(CreateCustomDialogService);
  private readonly createInitiativeService = inject(CreateInitiativeDialogService);
  private readonly exceedTicketDialogService = inject(ExceedTicketDialogService);

  // Convert observables to signals for reactivity
  private cancelReorderDialogOpen = toSignal(this.cancelDialogService.isOpen$, { initialValue: false });
  private createCustomFormDialogOpen = toSignal(this.createCustomFormService.isOpen$, { initialValue: false });
  private createInitiativeDialogOpen = toSignal(this.createInitiativeService.isOpen$, { initialValue: false });
  private exceedTicketDialogOpen = toSignal(this.exceedTicketDialogService.isOpen$, { initialValue: false });

  // Computed signal to determine when any dialog needs to be shown
  private shouldLoadDialogManager = computed(() => {
    return this.cancelReorderDialogOpen() ||
           this.createCustomFormDialogOpen() ||
           this.createInitiativeDialogOpen() ||
           this.exceedTicketDialogOpen();
  });

  constructor() {
    this.initializeDialogListener();
  }

  /**
   * Sets up reactive effect to load/unload dialog manager based on dialog state
   */
  private initializeDialogListener(): void {
    effect(() => {
      if (this.shouldLoadDialogManager()) {
        this.loadDialogManager();
      } else {
        this.destroyDialogManager();
      }
    });
  }

  /**
   * Dynamically loads and attaches DialogManagerComponent to the DOM
   */
  private async loadDialogManager(): Promise<void> {
    if (this.componentRef) return; // Already loaded

    // Dynamically import the component (code splitting benefit)
    const { DialogManagerComponent } = await import(
      '../components/confirmation-dialog-manager/confirmation-dialog-manager.component'
    );

    // Create component instance
    this.componentRef = createComponent(DialogManagerComponent, {
      environmentInjector: this.injector,
    });

    // Attach to Angular's change detection
    this.appRef.attachView(this.componentRef.hostView);

    // Get DOM element and append to body
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
  }

  /**
   * Removes and destroys DialogManagerComponent
   */
  private destroyDialogManager(): void {
    if (this.componentRef) {
      const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
      
      // Remove from DOM
      if (domElem && domElem.parentNode) {
        domElem.parentNode.removeChild(domElem);
      }

      // Detach from Angular and destroy
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}
